import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { lookupsJson } from '@ourbranch/lookups';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import { useFormikContext, getIn } from 'formik';

import Field from 'core/components/form/form.v2';
import { currencyFormatter, dateFormatter } from 'core/helpers/formatters';
import { usePrevious } from 'core/hooks/usePrevious';
import { useToast } from 'core/components/toast';
import { AppliedTag } from 'common/components/farm/applied-tag';
import { RequiresWaterBackupTag } from 'common/components/farm/requires-water-backup-tag';
import { SubRiskCollapsibleSection } from './sub-risk-collapsible-section';
import useStyles from './sub-risk.styles';

const INLAND_FLOOD_DEDUCTIBLE = 250;
const occurrenceLimitOptions = [
  { id: 0, value: 'No coverage' },
  ...lookupsJson.farm.inlandFloodLimit.filter((option) => option.id !== 0)
];

const deductibleOptions = [
  { id: INLAND_FLOOD_DEDUCTIBLE, value: currencyFormatter(INLAND_FLOOD_DEDUCTIBLE, { removeDecimal: true }) }
];

const InlandFloodCoverageForm = ({ fieldName, disabled }) => {
  const classes = useStyles();
  const { values, initialValues, setFieldValue } = useFormikContext();
  const toast = useToast();

  const {
    inlandFloodOccurrenceLimit: occurrenceLimit,
    inlandFloodAggregateLimit: aggregateLimit,
    inlandFloodDeductible: deductible,
    inlandFloodInceptionDate: inceptionDate,
    inlandFloodRiskScore: riskScore,
    coverageWaterBackup: waterBackup,
    inlandFloodEligible: eligible
  } = getIn(values, fieldName) || {};

  const appliedOccurrence = Number(occurrenceLimit) || 0;
  const isApplied = appliedOccurrence > 0;

  const initialCoverages = getIn(initialValues, fieldName) || {};
  const standaloneWaterBackup =
    Number(initialCoverages.inlandFloodOccurrenceLimit) > 0 ? null : initialCoverages.coverageWaterBackup || null;

  useEffect(() => {
    if (appliedOccurrence > 0) {
      const expectedAggregate = appliedOccurrence * 2;
      if (aggregateLimit !== expectedAggregate) {
        setFieldValue(`${fieldName}.inlandFloodAggregateLimit`, expectedAggregate);
      }
      if (deductible !== INLAND_FLOOD_DEDUCTIBLE) {
        setFieldValue(`${fieldName}.inlandFloodDeductible`, INLAND_FLOOD_DEDUCTIBLE);
      }
    } else {
      // "No coverage" is 0, not null: the CFM rater defaults and the post-data rule both
      // settle these fields to 0 when the occurrence limit is off, so clearing them to null
      // here leaves the form permanently dirty against the rated offer — the offer never
      // settles after "Update Offer Price" and can't advance to underwriting (#34775).
      // Guard on truthiness so an already-0 field writes nothing (the effect converges).
      if (aggregateLimit) {
        setFieldValue(`${fieldName}.inlandFloodAggregateLimit`, 0);
      }
      if (deductible) {
        setFieldValue(`${fieldName}.inlandFloodDeductible`, 0);
      }
    }
  }, [appliedOccurrence, aggregateLimit, deductible, fieldName, setFieldValue]);

  useEffect(() => {
    if (appliedOccurrence <= 0 || waterBackup === appliedOccurrence) {
      return;
    }
    setFieldValue(`${fieldName}.coverageWaterBackup`, appliedOccurrence);
    toast.notify({
      type: 'success',
      message: `Water Backup automatically set to ${currencyFormatter(appliedOccurrence, {
        removeDecimal: true
      })} to match Inland Flood.`
    });
  }, [appliedOccurrence, waterBackup, fieldName, setFieldValue, toast]);

  const wasApplied = usePrevious(isApplied);
  useEffect(() => {
    if (wasApplied && !isApplied && (waterBackup || null) !== standaloneWaterBackup) {
      setFieldValue(`${fieldName}.coverageWaterBackup`, standaloneWaterBackup);
      toast.notify({
        type: 'success',
        message: standaloneWaterBackup
          ? `Water Backup restored to ${currencyFormatter(standaloneWaterBackup, {
              removeDecimal: true
            })} after Inland Flood was removed.`
          : 'Water Backup cleared because Inland Flood was removed.'
      });
    }
  }, [wasApplied, isApplied, waterBackup, standaloneWaterBackup, fieldName, setFieldValue, toast]);

  if (eligible === false) {
    return null;
  }

  return (
    <SubRiskCollapsibleSection
      type="secondary"
      content={<div className={classes.headerText}>Inland flood coverage</div>}
      contentRight={isApplied ? <AppliedTag /> : <RequiresWaterBackupTag coverageWaterBackup={waterBackup} />}
      initialOpen={false}
    >
      <Grid container spacing={2}>
        <Field
          xs={4}
          type="select"
          id={`${fieldName}.inlandFloodOccurrenceLimit`}
          name={`${fieldName}.inlandFloodOccurrenceLimit`}
          mode="dark"
          label="Occurrence limit"
          options={occurrenceLimitOptions}
          disabled={disabled}
        />
        <Field
          xs={4}
          type="select"
          id={`${fieldName}.inlandFloodAggregateLimit`}
          name={`${fieldName}.inlandFloodAggregateLimit`}
          mode="dark"
          label="Aggregate limit"
          options={lookupsJson.farm.inlandFloodAggregateLimit}
          disabled
        />
        <Field
          xs={4}
          type="select"
          id={`${fieldName}.inlandFloodDeductible`}
          name={`${fieldName}.inlandFloodDeductible`}
          mode="dark"
          label="Inland flood deductible"
          options={deductibleOptions}
          disabled
        />
        <Field
          xs={6}
          type="value"
          id={`${fieldName}.inlandFloodInceptionDate`}
          mode="dark"
          label="Inception date"
          value={inceptionDate ? dateFormatter(inceptionDate) : '—'}
        />
        <Field
          xs={6}
          type="value"
          id={`${fieldName}.inlandFloodRiskScore`}
          mode="dark"
          label="Inland flood risk score"
          value={riskScore != null ? String(riskScore) : '—'}
        />
      </Grid>
    </SubRiskCollapsibleSection>
  );
};

InlandFloodCoverageForm.propTypes = {
  fieldName: PropTypes.string.isRequired,
  disabled: PropTypes.bool
};

InlandFloodCoverageForm.defaultProps = {
  disabled: false
};

export default observer(InlandFloodCoverageForm);
